import { Fragment, useEffect } from "react";
import ErrorLabel from "../base/ErrorLabel";
import FieldLabel from "../FieldLabel";
import DateInput from "../DateInput";
import { getSelectOptions } from "../../../../utils/selectHelpers";

const AddressFields = ({
  formState,
  setFormState,
  isMulti,
  fieldName = "businessAddress",
  fieldNames = {
    address: "address",
    suiteAddress: "suiteAddress",
    city: "city",
    stateAddress: "stateAddress",
    zipCode: "zipCode",
  },
  formMethods = null,
  onDatePickerOpen,
  onDatePickerClose,
}) => {
  // Set default country value when component mounts
  useEffect(() => {
    if (formMethods) {
      formMethods.setValue("country", "US", {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [formMethods]);

  // Function to consolidate address fields into a single object (only when not using formMethods)
  const updateAddressField = (updater) => {
    if (formMethods) {
      // When using formMethods, we still need to update the consolidated field
      // Get current values from formMethods
      const currentValues = formMethods.getValues();
      const newState = updater(currentValues);

      // Update the consolidated address field
      const consolidatedAddress = {
        address: newState[fieldNames.address] || "",
        suiteAddress: newState[fieldNames.suiteAddress] || "",
        city: newState[fieldNames.city] || "",
        stateAddress: newState[fieldNames.stateAddress] || "",
        country: newState.country || "US",
        zipCode: newState[fieldNames.zipCode] || "",
        fromDate: newState.fromDate || "",
        toDate: newState.toDate || "",
      };

      // Update the consolidated field in formMethods
      // Set consolidated object but avoid triggering group-level validation here
      formMethods.setValue(fieldName, consolidatedAddress, {
        shouldValidate: false,
        shouldDirty: true,
      });

      return;
    }

    setFormState((prev) => {
      const newState = updater(prev);

      // Also update the consolidated address field using dynamic field names
      const consolidatedAddress = {
        address: newState[fieldNames.address] || "",
        suiteAddress: newState[fieldNames.suiteAddress] || "",
        city: newState[fieldNames.city] || "",
        stateAddress: newState[fieldNames.stateAddress] || "",
        country: newState.country || "US",
        zipCode: newState[fieldNames.zipCode] || "",
        fromDate: newState.fromDate || "",
        toDate: newState.toDate || "",
      };

      return {
        ...newState,
        [fieldName]: consolidatedAddress,
      };
    });
  };

  const validateRequired = (name, value, message) => {
    if (!formMethods) return;
    const isEmpty =
      value === undefined || value === null || String(value).trim() === "";
    if (isEmpty) {
      formMethods.setError(name, { type: "manual", message });
    } else {
      formMethods.clearErrors(name);
    }
  };

  return (
    <Fragment>
      <div className="form-field">
        <FieldLabel
          label="Street Address"
          htmlFor={fieldNames.address}
        />
        <input
          type="text"
          id={fieldNames.address}
          className="form-input"
          {...(formMethods
            ? (() => {
                const registerResult = formMethods.register(
                  fieldNames.address,
                  { required: "Street address is required" }
                );

                // Override onChange to also update consolidated field
                return {
                  ...registerResult,
                  onChange: (e) => {
                    // Call the original onChange
                    registerResult.onChange(e);

                    // Also update consolidated field
                    updateAddressField((prev) => ({
                      ...prev,
                      [fieldNames.address]: e.target.value,
                    }));
                  },
                  onBlur: (e) => {
                    if (registerResult.onBlur) registerResult.onBlur(e);
                    validateRequired(
                      fieldNames.address,
                      e.target.value,
                      "Street address is required"
                    );
                  },
                };
              })()
            : {
                name: fieldNames.address,
                value: formState[fieldNames.address],
                onChange: (e) => {
                  updateAddressField((prev) => ({
                    ...prev,
                    [fieldNames.address]: e.target.value,
                  }));
                },
              })}
        />
        {formMethods && formMethods.formState.errors[fieldNames.address] && (
          <ErrorLabel fieldName="Street Address">
            {formMethods.formState.errors[fieldNames.address].message}
          </ErrorLabel>
        )}
      </div>
      <div className="form-field">
        <FieldLabel
          label="Apt, Suite, Fl."
          htmlFor={fieldNames.suiteAddress}
        />
        <input
          type="text"
          id={fieldNames.suiteAddress}
          name={fieldNames.suiteAddress}
          className="form-input"
          {...(formMethods
            ? formMethods.register(fieldNames.suiteAddress)
            : {
                value: formState[fieldNames.suiteAddress],
                onChange: (e) =>
                  updateAddressField((prev) => ({
                    ...prev,
                    [fieldNames.suiteAddress]: e.target.value,
                  })),
              })}
        />
        {formMethods &&
          formMethods.formState.errors[fieldNames.suiteAddress] && (
            <ErrorLabel fieldName="Apt, Suite, Fl.">
              {formMethods.formState.errors[fieldNames.suiteAddress].message}
            </ErrorLabel>
          )}
      </div>
      <div className="multiInputRaw">
        <div className="form-field">
          <FieldLabel
            label="City"
            htmlFor={fieldNames.city}
          />
          <input
            type="text"
            id={fieldNames.city}
            className="form-input"
            {...(formMethods
              ? (() => {
                  const registerResult = formMethods.register(fieldNames.city, {
                    required: "City is required",
                  });

                  // Override onChange to also update consolidated field
                  return {
                    ...registerResult,
                    onChange: (e) => {
                      // Call the original onChange
                      registerResult.onChange(e);

                      // Also update consolidated field
                      updateAddressField((prev) => ({
                        ...prev,
                        [fieldNames.city]: e.target.value,
                      }));
                    },
                    onBlur: (e) => {
                      if (registerResult.onBlur) registerResult.onBlur(e);
                      validateRequired(
                        fieldNames.city,
                        e.target.value,
                        "City is required"
                      );
                    },
                  };
                })()
              : {
                  name: fieldNames.city,
                  value: formState[fieldNames.city],
                  onChange: (e) =>
                    updateAddressField((prev) => ({
                      ...prev,
                      [fieldNames.city]: e.target.value,
                    })),
                })}
          />
          {formMethods && formMethods.formState.errors[fieldNames.city] && (
            <ErrorLabel fieldName="City">
              {formMethods.formState.errors[fieldNames.city].message}
            </ErrorLabel>
          )}
        </div>
        <div className="form-field">
          <FieldLabel
            label="Country"
            htmlFor="country"
          />
          <input
            type="text"
            id="country"
            name="country"
            className="form-input"
            {...(formMethods
              ? formMethods.register("country", { value: "US" })
              : {
                  defaultValue: "US",
                })}
            disabled
          />
        </div>
      </div>

      <div className="multiInputRaw">
        <div className="form-field">
          <FieldLabel
            label="State"
            htmlFor={fieldNames.stateAddress}
          />
          <select
            id={fieldNames.stateAddress}
            name={fieldNames.stateAddress}
            className="form-input"
            required
            {...(formMethods
              ? (() => {
                  const registerResult = formMethods.register(
                    fieldNames.stateAddress,
                    { required: "State is required" }
                  );

                  // Override onChange to also update consolidated field
                  return {
                    ...registerResult,
                    onChange: (e) => {
                      // Call the original onChange
                      registerResult.onChange(e);

                      // Also update consolidated field
                      updateAddressField((prev) => ({
                        ...prev,
                        [fieldNames.stateAddress]: e.target.value,
                      }));
                    },
                    onBlur: (e) => {
                      if (registerResult.onBlur) registerResult.onBlur(e);
                      validateRequired(
                        fieldNames.stateAddress,
                        e.target.value,
                        "State is required"
                      );
                    },
                  };
                })()
              : {
                  value: formState[fieldNames.stateAddress] || "",
                  onChange: (e) => {
                    updateAddressField((prev) => ({
                      ...prev,
                      [fieldNames.stateAddress]: e.target.value,
                    }));
                  },
                })}
          >
            <option value="">Select State</option>
            {(getSelectOptions("usaStates") || []).map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formMethods &&
            formMethods.formState.errors[fieldNames.stateAddress] && (
              <ErrorLabel fieldName="State">
                {formMethods.formState.errors[fieldNames.stateAddress].message}
              </ErrorLabel>
            )}
        </div>

        <div className="form-field">
          <FieldLabel
            label="Zip Code"
            htmlFor={fieldNames.zipCode}
          />
          <input
            type="text"
            id={fieldNames.zipCode}
            className="form-input"
            {...(formMethods
              ? (() => {
                  const registerResult = formMethods.register(
                    fieldNames.zipCode,
                    { required: "Zip code is required" }
                  );

                  // Override onChange to also update consolidated field
                  return {
                    ...registerResult,
                    onChange: (e) => {
                      // Call the original onChange
                      registerResult.onChange(e);

                      // Also update consolidated field
                      updateAddressField((prev) => ({
                        ...prev,
                        [fieldNames.zipCode]: e.target.value,
                      }));
                    },
                    onBlur: (e) => {
                      if (registerResult.onBlur) registerResult.onBlur(e);
                      validateRequired(
                        fieldNames.zipCode,
                        e.target.value,
                        "Zip code is required"
                      );
                    },
                  };
                })()
              : {
                  name: fieldNames.zipCode,
                  value: formState[fieldNames.zipCode],
                  onChange: (e) =>
                    updateAddressField((prev) => ({
                      ...prev,
                      [fieldNames.zipCode]: e.target.value,
                    })),
                })}
          />
          {formMethods && formMethods.formState.errors[fieldNames.zipCode] && (
            <ErrorLabel fieldName="Zip Code">
              {formMethods.formState.errors[fieldNames.zipCode].message}
            </ErrorLabel>
          )}
        </div>
      </div>
      {isMulti && (
        <Fragment>
          <div className="multiInputRaw">
            <div className="form-field">
              <FieldLabel
                label="From"
                htmlFor="fromDate"
              />
              <DateInput
                id="fromDate"
                name="fromDate"
                label="From Date"
                value={formMethods ? formMethods.getValues("fromDate") : formState.fromDate}
                maxDate={new Date()}
                onOpen={onDatePickerOpen}
                onClose={onDatePickerClose}
                onChange={(e) => {
                  if (formMethods) {
                    formMethods.setValue("fromDate", e.target.value, { shouldValidate: true, shouldDirty: true });
                  }
                  updateAddressField((prev) => ({
                    ...prev,
                    fromDate: e.target.value,
                  }));
                }}
                onBlur={() => {
                  if (formMethods) {
                    formMethods.trigger("fromDate");
                  }
                }}
              />
              {formMethods && formMethods.formState.errors.fromDate && (
                <ErrorLabel fieldName="From Date">
                  {formMethods.formState.errors.fromDate.message}
                </ErrorLabel>
              )}
            </div>
            <div className="form-field">
              <FieldLabel
                label="To"
                htmlFor="toDate"
              />
              <DateInput
                id="toDate"
                name="toDate"
                label="To Date"
                value={formMethods ? formMethods.getValues("toDate") : formState.toDate}
                maxDate={new Date()}
                onOpen={onDatePickerOpen}
                onClose={onDatePickerClose}
                onChange={(e) => {
                  if (formMethods) {
                    formMethods.setValue("toDate", e.target.value, { shouldValidate: true, shouldDirty: true });
                  }
                  updateAddressField((prev) => ({
                    ...prev,
                    toDate: e.target.value,
                  }));
                }}
                onBlur={() => {
                  if (formMethods) {
                    formMethods.trigger("toDate");
                  }
                }}
              />
              {formMethods && formMethods.formState.errors.toDate && (
                <ErrorLabel fieldName="To Date">
                  {formMethods.formState.errors.toDate.message}
                </ErrorLabel>
              )}
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default AddressFields;
